import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ArrowUpRight, Sparkles } from "lucide-react";
import { fetchQuizQuestions, submitQuiz } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { formatINR } from "@/lib/format";
import Seo from "@/components/seo/Seo";

export default function Quiz() {
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(-1); // -1 intro, 0..N questions, N result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { add, setOpen } = useCart();

  useEffect(() => {
    fetchQuizQuestions().then((d) => setQuestions(d.questions || [])).catch(() => {});
  }, []);

  const total = questions.length;
  const current = step >= 0 && step < total ? questions[step] : null;
  const progress = total ? Math.min(((step + 1) / total) * 100, 100) : 0;

  const onChoose = (qid, value) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    if (step + 1 < total) {
      setTimeout(() => setStep(step + 1), 280);
    } else {
      runSubmit(next);
    }
  };

  const runSubmit = async (final) => {
    setLoading(true);
    try {
      const r = await submitQuiz(final);
      setResult(r);
      setStep(total); // results screen
    } catch (e) {
      toast("The quiz could not complete. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setResult(null);
    setStep(-1);
  };

  return (
    <div data-testid="quiz-page" className="pt-28 md:pt-32 min-h-[90vh] bg-bone">
      <Seo
        path="/quiz"
        title="The Ritual Finder"
        description="Eight quiet questions. A bespoke ritual recommendation drawn from your sleep, stress, energy, and intentions."
      />
      {/* Top progress */}
      {step >= 0 && step < total && (
        <div className="px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between mb-3">
            <p className="overline text-stone">
              {String(step + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </p>
            <button onClick={restart} className="overline text-stone hover:text-ink" data-testid="quiz-restart">
              Start over
            </button>
          </div>
          <div className="h-px bg-hairline-subtle relative overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full bg-ink transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* INTRO */}
      {step === -1 && (
        <section className="px-6 md:px-12 lg:px-16 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <p className="overline mb-6">The Ritual Finder · 8 quiet questions</p>
              <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-light leading-[0.92] tracking-[-0.03em] text-ink">
                A short<br />
                <em className="italic text-moss">consultation,</em><br />
                in eight breaths.
              </h1>
              <p className="mt-10 max-w-xl text-charcoal text-lg md:text-xl font-light leading-[1.7]">
                Not a diagnosis. A reading. Tell us how the day finds you and
                we will point to a single ritual, an essay, and a few objects
                worth considering — for this season only.
              </p>
              <div className="mt-10 flex items-center gap-6">
                <button
                  onClick={() => setStep(0)}
                  data-testid="quiz-start"
                  className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-bone bg-ink px-6 py-4 hover:bg-moss transition-colors"
                >
                  Begin <ArrowRight size={14} />
                </button>
                <p className="overline text-stone hidden md:block">~ 90 seconds</p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <ul className="space-y-3 border-t border-hairline pt-6">
                {[
                  "Sleep & energy",
                  "Stress & emotional state",
                  "Movement & recovery",
                  "Daily routine & intention",
                ].map((t, i) => (
                  <li key={t} className="flex items-center gap-4 text-charcoal" data-testid={`quiz-topic-${i}`}>
                    <span className="font-mono text-xs text-stone w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display italic text-xl text-ink">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* QUESTION */}
      {current && (
        <section className="px-6 md:px-12 lg:px-16 py-14 md:py-24 max-w-5xl">
          <p className="overline mb-6 text-moss">{current.overline}</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0] tracking-tight text-ink animate-fade-up" key={current.id}>
            {current.question}
          </h2>

          <ul className="mt-12 space-y-2">
            {current.options.map((opt) => {
              const isActive = answers[current.id] === opt.value;
              return (
                <li key={opt.value}>
                  <button
                    onClick={() => onChoose(current.id, opt.value)}
                    data-testid={`quiz-option-${current.id}-${opt.value}`}
                    className={`w-full text-left grid grid-cols-12 gap-6 py-5 border-t border-hairline group transition-colors ${
                      isActive ? "bg-ink text-bone -mx-3 px-3" : "hover:bg-clay/40 -mx-3 px-3"
                    }`}
                  >
                    <span className={`col-span-1 font-mono text-xs mt-2 ${isActive ? "text-bone/60" : "text-stone"}`}>
                      {String(current.options.indexOf(opt) + 1).padStart(2, "0")}
                    </span>
                    <span className={`col-span-10 font-display text-2xl md:text-3xl leading-snug ${isActive ? "italic" : "group-hover:italic"}`}>
                      {opt.label}
                    </span>
                    <span className={`col-span-1 self-center ${isActive ? "text-bone" : "text-stone group-hover:text-ink"}`}>
                      <ArrowRight size={16} />
                    </span>
                  </button>
                </li>
              );
            })}
            <li className="border-t border-hairline" />
          </ul>

          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="overline inline-flex items-center gap-2 mt-10 text-stone hover:text-ink"
              data-testid="quiz-back"
            >
              <ArrowLeft size={12} /> Previous
            </button>
          )}
        </section>
      )}

      {/* LOADING */}
      {loading && step >= total && (
        <section className="px-6 md:px-12 lg:px-16 py-32 text-center">
          <Sparkles className="mx-auto mb-6 text-moss animate-pulse" size={28} />
          <p className="overline text-stone">Composing your reading…</p>
        </section>
      )}

      {/* RESULT */}
      {result && (
        <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="quiz-result">
          <p className="overline mb-6 text-moss">Your reading</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.0] tracking-tight text-ink">
            The current season<br />
            asks for <em className="italic text-moss">{result.primary_category.toLowerCase()}.</em>
          </h2>
          <p className="mt-6 font-display italic text-xl md:text-2xl text-charcoal max-w-2xl">
            With a quieter undertone of {result.secondary_category?.toLowerCase() || "—"}.
          </p>

          {/* Primary ritual */}
          {result.primary_ritual && (
            <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center border-t border-hairline pt-12">
              <Link to={`/rituals/${result.primary_ritual.slug}`} className="lg:col-span-6 img-zoom aspect-[4/5] overflow-hidden group" data-testid="quiz-primary-ritual">
                <img src={result.primary_ritual.cover_image} alt={result.primary_ritual.title} className="w-full h-full object-cover" />
              </Link>
              <div className="lg:col-span-5 lg:col-start-8">
                <p className="overline mb-4">Begin with this practice</p>
                <h3 className="font-display text-3xl md:text-5xl font-light text-ink leading-tight">
                  {result.primary_ritual.title}
                </h3>
                <p className="mt-5 text-charcoal font-light leading-relaxed text-lg max-w-md">
                  {result.primary_ritual.description}
                </p>
                <div className="mt-6 border-t border-hairline pt-4 flex items-center gap-6">
                  <p className="overline">Duration · {result.primary_ritual.duration}</p>
                </div>
                <Link
                  to={`/rituals/${result.primary_ritual.slug}`}
                  className="inline-flex items-center gap-3 mt-6 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-bone bg-ink px-5 py-3 hover:bg-moss transition-colors"
                >
                  Open the ritual <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          )}

          {/* Article */}
          {result.article && (
            <div className="mt-20 border-t border-hairline pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <p className="overline mb-4">Read alongside</p>
                <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                  {result.article.title}
                </h3>
                <p className="font-display italic text-lg text-charcoal mt-3">{result.article.subtitle}</p>
                <Link to={`/journal/${result.article.slug}`} className="inline-flex items-center gap-2 mt-5 text-[0.7rem] tracking-[0.22em] uppercase text-ink link-underline">
                  Read the essay <ArrowUpRight size={12} />
                </Link>
              </div>
              <Link to={`/journal/${result.article.slug}`} className="lg:col-span-6 lg:col-start-7 img-zoom aspect-[16/10] overflow-hidden">
                <img src={result.article.cover_image} alt={result.article.title} className="w-full h-full object-cover" />
              </Link>
            </div>
          )}

          {/* Products */}
          {result.products && result.products.length > 0 && (
            <div className="mt-20 border-t border-hairline pt-12">
              <p className="overline mb-8">Objects that may support</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                {result.products.map((p) => (
                  <div key={p.slug} className="group" data-testid={`quiz-product-${p.slug}`}>
                    <Link to={`/shop/${p.slug}`} className="block img-zoom aspect-[4/5] bg-clay overflow-hidden">
                      <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />
                    </Link>
                    <div className="pt-4 flex items-start justify-between">
                      <div>
                        <p className="overline mb-1">{p.category}</p>
                        <Link to={`/shop/${p.slug}`} className="font-display text-xl text-ink hover:italic transition-all">{p.name}</Link>
                      </div>
                      <p className="text-sm font-mono text-charcoal whitespace-nowrap">{formatINR(p.price)}</p>
                    </div>
                    <button
                      onClick={() => { add(p, 1); setOpen(true); }}
                      data-testid={`quiz-add-${p.slug}`}
                      className="mt-3 text-[0.7rem] tracking-[0.22em] uppercase text-ink link-underline"
                    >
                      Add to bag
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-20 border-t border-hairline pt-10 flex flex-wrap gap-6">
            <button
              onClick={restart}
              data-testid="quiz-restart"
              className="text-[0.72rem] tracking-[0.22em] uppercase text-ink link-underline"
            >
              Retake the reading
            </button>
            <Link to="/rituals" className="text-[0.72rem] tracking-[0.22em] uppercase text-ink link-underline">
              All rituals
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
